import React, { useMemo } from 'react';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { Icon } from '../../../concepts/resources/Icon';
import { Button, Toggle, usePrompt } from '../../../ui';
import { FixedScroller } from '../../../ui/Scrollers';
import { Table } from '../../../ui/Table';
import { useResolve } from '../../Container';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../../popups/Popup';
import { boardSettingSet } from '../boardSettings/BoardSettingCommands';
import { useBoardSettingGroup } from '../boardSettings/BoardSettingHook';
import { JiraCard } from './JiraCard';

const standard = [
    {
        key: 'dueDate',
        name: 'Due Date',
        type: 'date',
    }
];

const supportedTypes = new Set([
    'string',
    'number',
    'date',
    'sprint',
    'option',
]);

const useOptionalFields = () => {
    const objects = useResolve(ObjectManager);

    return useMemo(() => {
        const cards = Array.from(objects.getAll(JiraCard));
        const fields = Object.fromEntries(
            cards.flatMap(c => c.customs)
                .filter(f => supportedTypes.has(f.type))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(f => [f.key, f])
        );
        return [...standard, ...Object.values(fields)];
    }, []);
};

export const configureVisibleJiraCustomFieldsPopup = () => ({ ui: ConfigureVisibleJiraCustomFieldsPopup });

export const ConfigureVisibleJiraCustomFieldsPopup = ({ dismiss }: PopupProps) => {

    const promptRef = usePrompt({
        ignoreEnter: true,
        onAction: dismiss,
    });

    const dispatch = useResolve(CommandDispatcher);
    const fields = useOptionalFields();
    const values = {
        'customfield_10016': true,
        'customfield_10020': true,
        ...useBoardSettingGroup('jira'),
    };

    const onToggle = (key: string) => (value: boolean) => {
        dispatch(boardSettingSet, ['jira', key, value]);
    };

    return <Popup ref={promptRef} p="1">
        <Popup.Header>
            <Popup.IconFrame>
                <Icon src="deqo://icons/i8/color-jira" size="2" />
            </Popup.IconFrame>
            <Popup.Title>
                Configure Visible Fields
            </Popup.Title>
            <Popup.SubTitle>
                Choose which Jira fields are visible for this board.
            </Popup.SubTitle>
        </Popup.Header>
        <Popup.Content>
            <Flexer minWidth="32" height=".5" />
            <FixedScroller autoHide={false} maxHeight="24">
                <Table>
                    <Table.Body>
                        {fields.map((f, i) =>
                            <Table.Row key={i}>
                                <Table.Cell style={{ width: '32rem' }}>{f.name}</Table.Cell>
                                <Table.Cell>{f.type}</Table.Cell>
                                <Table.Cell>
                                    <Toggle value={!!values[f.key]} onAction={onToggle(f.key)} />
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </FixedScroller>
        </Popup.Content>
        <Popup.Actions>
            <Button onAction={dismiss} variant="common">Close</Button>
        </Popup.Actions>
        <Popup.Close onAction={dismiss} />
    </Popup>;
};
